import React, { useState } from 'react';
import axios from 'axios';
import { CheckCircle } from 'lucide-react';

const EMAILJS_SERVICE_ID  = 'service_v0qee4p';
   const EMAILJS_TEMPLATE_ID = 'template_7lgqkxq';
   const EMAILJS_PUBLIC_KEY  = '4X6mznULVxEgJDQas';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const QuerySection = () => {
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    company_name: '',
    nature_of_query: 'GST & Indirect Tax',
    detailed_query: '',
    preferred_contact_mode: 'Email'
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [captchaChecked, setCaptchaChecked] = useState(false);

  const natureOptions = [
    'GST & Indirect Tax',
    'Income Tax & Direct Tax',
    'Audit & Assurance',
    'Business Setup & Registration',
    'Corporate Advisory',
    'International / NRI Services',
    'Project Finance',
    'Other'
  ];

  const contactModes = ['Email', 'Phone', 'WhatsApp', 'Video Call'];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.full_name.trim()) newErrors.full_name = 'Full name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Email is invalid';
    if (!formData.phone.trim()) newErrors.phone = 'Phone number is required';
    if (!formData.detailed_query.trim() || formData.detailed_query.length < 20) {
      newErrors.detailed_query = 'Please provide detailed query (minimum 20 characters)';
    }
    if (!captchaChecked) newErrors.captcha = 'Please verify you are not a robot';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setIsSubmitting(true);
    
    try {
      await axios.post(`${BACKEND_URL}/api/query`, formData);
      setSubmitSuccess(true);
      // Reset form
      setFormData({
        full_name: '',
        email: '',
        phone: '',
        company_name: '',
        nature_of_query: 'GST & Indirect Tax',
        detailed_query: '',
        preferred_contact_mode: 'Email'
      });
      setCaptchaChecked(false);
      
      // Hide success message after 5 seconds
      setTimeout(() => setSubmitSuccess(false), 5000);
    } catch (error) {
      console.error('Error submitting query:', error);
      alert('Failed to submit query. Please try again or call us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="relative py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#0D1B2A] via-[#1a2d42] to-[#0D1B2A]">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `repeating-linear-gradient(0deg, #C9A84C, #C9A84C 1px, transparent 1px, transparent 20px),
                           repeating-linear-gradient(90deg, #C9A84C, #C9A84C 1px, transparent 1px, transparent 20px)`
        }}></div>
      </div>
      
      <div className="max-w-4xl mx-auto relative z-10">
        <div className="text-center mb-16">
          <h2 className="text-4xl sm:text-5xl font-playfair font-bold text-[#C9A84C] mb-4">
            Submit Your Query
          </h2>
          <div className="w-24 h-1 bg-[#C9A84C] mx-auto mb-6"></div>
          <p className="text-gray-300 font-dmsans">
            Our team will revert within 2 working days. For urgent matters, please call our office directly.
          </p>
        </div>

        {submitSuccess && (
          <div className="mb-8 p-6 bg-green-50 border-l-4 border-green-500 rounded-lg flex items-start gap-4">
            <CheckCircle className="text-green-500 flex-shrink-0 mt-1" size={24} />
            <div>
              <h3 className="font-playfair font-bold text-green-800 mb-2">Query Submitted Successfully!</h3>
              <p className="text-green-700 font-dmsans text-sm">
                Thank you for your query. Our team will revert within 2 working days. 
                For urgent matters please call our office directly.
              </p>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="bg-[#1a2d42] bg-opacity-60 backdrop-blur-sm p-8 rounded-lg shadow-lg border border-[#C9A84C] border-opacity-20">
          <div className="grid md:grid-cols-2 gap-6 mb-6">
            <div>
              <label className="block text-sm font-dmsans font-semibold text-gray-300 mb-2">
                Full Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="full_name"
                value={formData.full_name}
                onChange={handleChange}
                className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#C9A84C] ${
                  errors.full_name ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Your full name"
              />
              {errors.full_name && <p className="text-red-500 text-xs mt-1">{errors.full_name}</p>}
            </div>

            <div>
              <label className="block text-sm font-dmsans font-semibold text-gray-300 mb-2">
                Email Address <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#C9A84C] ${
                  errors.email ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="your.email@example.com"
              />
              {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-6">
            <div>
              <label className="block text-sm font-dmsans font-semibold text-gray-300 mb-2">
                Phone Number <span className="text-red-500">*</span>
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#C9A84C] ${
                  errors.phone ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="+91-XXXXXXXXXX"
              />
              {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
            </div>

            <div>
              <label className="block text-sm font-dmsans font-semibold text-gray-300 mb-2">
                Company / Organisation Name
              </label>
              <input
                type="text"
                name="company_name"
                value={formData.company_name}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#C9A84C]"
                placeholder="Optional"
              />
            </div>
          </div>

          <div className="mb-6">
            <label className="block text-sm font-dmsans font-semibold text-gray-300 mb-2">
              Nature of Query <span className="text-red-500">*</span>
            </label>
            <select
              name="nature_of_query"
              value={formData.nature_of_query}
              onChange={handleChange}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#C9A84C]"
            >
              {natureOptions.map((option, index) => (
                <option key={index} value={option}>{option}</option>
              ))}
            </select>
          </div>

          <div className="mb-6">
            <label className="block text-sm font-dmsans font-semibold text-gray-300 mb-2">
              Detailed Query <span className="text-red-500">*</span>
            </label>
            <textarea
              name="detailed_query"
              value={formData.detailed_query}
              onChange={handleChange}
              rows="5"
              className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#C9A84C] ${
                errors.detailed_query ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Please provide detailed information about your query..."
            ></textarea>
            {errors.detailed_query && <p className="text-red-500 text-xs mt-1">{errors.detailed_query}</p>}
          </div>

          <div className="mb-6">
            <label className="block text-sm font-dmsans font-semibold text-gray-300 mb-2">
              Preferred Mode of Contact
            </label>
            <div className="flex flex-wrap gap-4">
              {contactModes.map((mode, index) => (
                <label key={index} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="preferred_contact_mode"
                    value={mode}
                    checked={formData.preferred_contact_mode === mode}
                    onChange={handleChange}
                    className="w-4 h-4 text-[#C9A84C] focus:ring-[#C9A84C]"
                  />
                  <span className="font-dmsans text-gray-300">{mode}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="mb-6">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={captchaChecked}
                onChange={(e) => {
                  setCaptchaChecked(e.target.checked);
                  if (errors.captcha && e.target.checked) {
                    setErrors(prev => ({ ...prev, captcha: '' }));
                  }
                }}
                className="w-5 h-5 text-[#C9A84C] focus:ring-[#C9A84C] rounded"
              />
              <span className="font-dmsans text-gray-300">I am not a robot</span>
            </label>
            {errors.captcha && <p className="text-red-500 text-xs mt-1">{errors.captcha}</p>}
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className={`w-full py-4 rounded-lg font-dmsans font-bold text-lg transition-all duration-300 ${
              isSubmitting
                ? 'bg-gray-400 text-gray-700 cursor-not-allowed'
                : 'bg-[#C9A84C] text-[#0D1B2A] hover:bg-[#b8973d] transform hover:scale-105'
            }`}
          >
            {isSubmitting ? 'Submitting...' : 'Submit Query'}
          </button>

          <p className="text-xs text-gray-400 font-dmsans text-center mt-6 italic">
            All queries are kept strictly confidential. Submitting this form does not create a client-professional relationship.
          </p>
        </form>
      </div>
    </section>
  );
};

export default QuerySection;
